import { parseProducts, ProductItemsField, type CataloguePricingContext } from './ProductItemsField'
import { applyTableDefaults } from "./tableDefaults"
import { FormEvent, useEffect, useRef, useState, type ReactNode } from 'react'
import { Alert, Button, Form, Spinner } from 'react-bootstrap'
import type { Core, FieldOptions, ElementValidatedEvent } from '@form-validation/core'
import { decimalPattern, formatDecimalValue, defaultFieldValue, fieldCaption, tableValidationErrors, formPages, tableLines, type OrderDefaults, type OrderField, type OrderForm } from '@/components/forms/formModel'
import { tableValidationLifecycle } from './tableValidationLifecycle'
import { createFormValidation, isFormValid } from '@/helpers/formValidationHelper'

export function collectOrderJson(form: HTMLFormElement): string {
  const data = new FormData(form)
  const order: Record<string, string | string[]> = {}
  data.forEach((value, key) => {
    const element = form.elements.namedItem(key)
    const text = element instanceof HTMLInputElement && element.dataset.decimal === 'true'
      ? formatDecimalValue(String(value)) : String(value)
    const current = order[key]
    order[key] = current === undefined ? text : Array.isArray(current) ? [...current, text] : [current, text]
  })
  return JSON.stringify(order)
}

export function OrderFormPages({
  form,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
  pinned,
  defaults,
  answers,
  children,
  cataloguePricing,
}: {
  form: OrderForm
  cataloguePricing?: CataloguePricingContext
  busy?: boolean
  submitLabel: string
  onSubmit: (form: HTMLFormElement) => void
  onCancel?: () => void
  pinned?: boolean
  defaults?: OrderDefaults
  answers?: Record<string, string | string[]>
  children?: (page: ReactNode) => ReactNode
}) {
  const pages = formPages(form)
  const [index, setIndex] = useState(0)
  const ref = useRef<HTMLFormElement>(null)
  const fvRef = useRef<Core | null>(null)
  const [catalogueErrors, setCatalogueErrors] = useState<string[]>([])
  const [tableErrors, setTableErrors] = useState<Record<string, string>>({})
  const checkedTables = useRef(new Set<string>())
  const validatingPage = useRef(0)
  const tableValidation = useRef<ReturnType<typeof tableValidationLifecycle> | null>(null)
  const safeIndex = Math.min(index, Math.max(pages.length - 1, 0))

  useEffect(() => {
    validatingPage.current = safeIndex
  }, [safeIndex])

  useEffect(() => {
    const formElement = ref.current
    if (!formElement) return
    checkedTables.current.clear()
    setTableErrors({})
    const fields = orderValidators(form)
    const tables = form.fields.filter(field => field.type === 'table').map(field =>
      tableLines(field).flatMap(line => (field.columns ?? []).map(column => `${field.id}.${line.id}.${column.id}`)))
    const tableNames = new Set(tables.flat())
    const fv = createFormValidation(formElement, {
      fields,
      // Delay table errors until Next/Submit, then refresh dependent cells together.
      validationEvents: Object.fromEntries(Object.keys(fields).map(name => [name, tableNames.has(name) ? false : 'input blur change'])),
      excluded: (_field, element) => {
        const page = element.closest('[data-page-index]')
        if (!page) return false
        return Number(page.getAttribute('data-page-index')) !== validatingPage.current
      },
    })
    // Blank optional cells are allowed, but should not look like completed entries.
    const neutralEmptyCell = (data: unknown) => {
      const event = data as ElementValidatedEvent
      if (event.valid && tableNames.has(event.field) &&
          (event.element instanceof HTMLInputElement || event.element instanceof HTMLSelectElement)) {
        const element = event.element
        if (!element.value.trim()) {
          element.classList.remove('is-valid')
          element.closest('td')?.classList.remove('fv-plugins-bootstrap5-row-valid')
        }
      }
    }
    fv.on('core.element.validated', neutralEmptyCell)
    fvRef.current = fv
    const lifecycle = tableValidationLifecycle(fv, tables)
    tableValidation.current = lifecycle
    const changed = (event: Event) => {
      const target = event.target
      if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) {
        void lifecycle.changed(target.name)
        const table = form.fields.find(field => field.type === 'table' && target.name.startsWith(`${field.id}.`))
        if (table && checkedTables.current.has(table.id)) updateTableErrors([table])
      }
    }
    formElement.addEventListener('input', changed)
    formElement.addEventListener('change', changed)
    return () => {
      formElement.removeEventListener('input', changed)
      formElement.removeEventListener('change', changed)
      tableValidation.current = null
      fv.destroy()
      fvRef.current = null
    }
  }, [form])

  function updateTableErrors(tables: OrderField[]): boolean {
    if (!ref.current) return false
    const data = new FormData(ref.current)
    const errors = Object.fromEntries(tables.map(table => [table.id,
      tableValidationErrors(table, name => String(data.get(name) ?? ''))[table.id] ?? '']))
    setTableErrors(current => ({ ...current, ...errors }))
    return Object.values(errors).every(error => !error)
  }

  async function pageIsValid(pageIndex: number): Promise<boolean> {
    validatingPage.current = pageIndex
    const fv = fvRef.current
    if (!fv) return false
    const names = Object.keys(fv.getFields()).filter(name =>
      fv.getElements(name).some(element => Number(element.closest('[data-page-index]')?.getAttribute('data-page-index')) === pageIndex))
    tableValidation.current?.attempt(names)
    const pageFieldIds = new Set(pages[pageIndex]?.rows.flatMap(row => row.columns.flatMap(column => column.fieldIds)))
    const tables = form.fields.filter(field => field.type === 'table' && pageFieldIds.has(field.id))
    tables.forEach(table => checkedTables.current.add(table.id))
    const tablesValid = updateTableErrors(tables)
    const data = new FormData(ref.current!)
    const productErrors = cataloguePricing ? form.fields.filter(field => field.type === 'productItems' && pageFieldIds.has(field.id)).flatMap(field => {
      const doc = parseProducts(String(data.get(field.id) ?? ''))
      const label = field.label || 'Order items'
      if (field.required && !doc.lines.length) return [`Choose at least one catalogue item for ${label}.`]
      if (doc.lines.length && !doc.quoteHash) return [`Review prices for ${label} before continuing.`]
      return []
    }) : []
    setCatalogueErrors(productErrors)
    // Previously checked pages may now be excluded; discard their cached status too.
    for (const name of Object.keys(fv.getFields())) {
      if (!names.includes(name)) fv.updateFieldStatus(name, 'NotValidated')
    }
    return await isFormValid(fv) && tablesValid && productErrors.length === 0
  }

  async function goNext() {
    if (!await pageIsValid(safeIndex)) return
    setIndex(safeIndex + 1)
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const submitter = (event.nativeEvent as SubmitEvent).submitter
    if (busy || safeIndex < pages.length - 1 || submitter?.getAttribute('data-order-submit') !== 'true') return
    // currentTarget is cleared once this handler awaits.
    const form = event.currentTarget
    for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
      if (!await pageIsValid(pageIndex)) {
        setIndex(pageIndex)
        return
      }
    }
    onSubmit(form)
  }

  const pageBody = (
    <>
      {catalogueErrors.map(message => <Alert key={message} variant="danger">{message}</Alert>)}
      {pages.length > 1 && (
        <p className="text-body-secondary">
          Page {safeIndex + 1} of {pages.length}
          {pages[safeIndex]?.title?.trim() ? ` · ${pages[safeIndex].title}` : ''}
        </p>
      )}
      {pages.map((page, pageIndex) => (
        <div key={page.id} data-page-index={pageIndex} hidden={pageIndex !== safeIndex}>
          {page.rows.map((row) => (
            <div className="row g-3" key={row.id}>
              {row.columns.map((column) => (
                <div className={`col-12 col-md-${column.width}`} key={column.id}>
                  {column.fieldIds.map((id) => {
                    const field = form.fields.find((item) => item.id === id)
                    return field ? <OrderFieldInput key={field.id} field={field} defaults={defaults} answers={answers} tableError={tableErrors[field.id]} pricing={cataloguePricing} /> : null
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </>
  )
  const actions = (
    <div className="d-flex justify-content-end gap-2">
      {onCancel && <Button type="button" variant="outline-secondary" onClick={onCancel} disabled={busy}>Cancel</Button>}
      {pages.length > 1 && safeIndex > 0 && (
        <Button type="button" disabled={busy} variant="outline-primary" onClick={() => setIndex(safeIndex - 1)}>Back</Button>
      )}
      {safeIndex < pages.length - 1 ? (
        <Button key="next" type="button" disabled={busy} onClick={() => void goNext()}>Next</Button>
      ) : (
        <Button key="submit" type="submit" data-order-submit="true" disabled={busy} aria-busy={Boolean(busy)}>
          {busy && <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />}
          {submitLabel}
        </Button>
      )}
    </div>
  )
  const body = (
    <fieldset disabled={busy} inert={busy} className="border-0 p-0 m-0" style={{ minWidth: 0 }}>
      {children ? children(pageBody) : pageBody}
    </fieldset>
  )
  return (
    <Form ref={ref} autoComplete="off" noValidate onSubmit={(event) => void submit(event)} onKeyDown={(event) => {
      if (event.key === 'Enter' && event.target instanceof HTMLInputElement) event.preventDefault()
    }} className={`submission-form${pinned ? ' action-workflow-form' : ''}`}>
      {pinned ? <div className="action-workspace-scroll">{body}</div> : body}
      {pinned ? <footer className="action-workspace-footer">{actions}</footer> : <div className="mt-3">{actions}</div>}
      <span className="visually-hidden" role="status">{busy ? 'Saving form…' : ''}</span>
    </Form>
  )
}

function orderValidators(form: OrderForm): Record<string, FieldOptions> {
  const fields: Record<string, FieldOptions> = {}
  for (const field of form.fields) {
    const validators = field.type === 'table' || field.type === 'productItems' ? null : inputValidators(field)
    if (validators) fields[field.id] = { validators }
    if (field.allowOther && (field.type === 'select' || field.type === 'radio' || field.type === 'checklist')) {
      fields[`${field.id}.other`] = {
        validators: {
          callback: {
            message: 'Enter the other value',
            callback: (input: { value: string; element: HTMLElement }) => otherValueOk(input, field),
          },
        },
      }
    }
    for (const line of tableLines(field)) {
      for (const column of field.columns ?? []) {
        const key = `${field.id}.${line.id}.${column.id}`
        fields[key] = { validators: {
          ...inputValidators({ ...column, required: false }),
          callback: {
            message: 'Check the table row',
            callback: (input: { element: HTMLInputElement }) => {
              const data = new FormData(input.element.form!)
              const errors = tableValidationErrors(field, name => String(data.get(name) ?? ''))
              return { valid: !errors[key], message: errors[key] }
            },
          },
        } }
      }
    }
  }
  return fields
}

function inputValidators(field: OrderField): FieldOptions['validators'] | null {
  const validators: NonNullable<FieldOptions['validators']> = {}
  if (field.required && !['title', 'heading', 'label'].includes(field.type)) {
    validators.notEmpty = { message: field.type === 'checklist' || field.type === 'radio' || field.type === 'select' || field.type === 'checkbox' ? 'Choose an option' : 'This field is required' }
  }
  if (field.type === 'email') validators.emailAddress = { message: 'Enter a valid email address' }
  if (field.type === 'number') validators.numeric = { message: 'Enter a number' }
  if (field.type === 'decimal') validators.regexp = { regexp: decimalPattern, message: 'Enter a number with up to 2 decimal places' }
  return Object.keys(validators).length ? validators : null
}

function otherValueOk(input: { value: string; element: HTMLElement }, field: OrderField): boolean {
  if (field.type === 'select') {
    const named = (input.element as HTMLInputElement).form?.elements.namedItem(field.id)
    const value = named && 'value' in named ? String(named.value) : ''
    if (value !== 'Other') return true
    return input.value.trim() !== ''
  }
  const other = input.element.closest('[data-choice]')?.querySelector<HTMLInputElement>('[data-other="true"]')
  if (!other?.checked) return true
  return input.value.trim() !== ''
}

type Answers = Record<string, string | string[]>
const answerText = (answers: Answers | undefined, key: string) => { const value = answers?.[key]; return Array.isArray(value) ? value[0] ?? "" : value ?? "" }
const answerIncludes = (answers: Answers | undefined, key: string, value: string) => { const selected = answers?.[key]; return Array.isArray(selected) ? selected.includes(value) : selected === value }

function ChoiceList({ field, shown, answers }: { field: OrderField; shown: string; answers?: Answers }) {
  const groupRef = useRef<HTMLDivElement>(null)
  const [otherOn, setOtherOn] = useState(answerIncludes(answers, field.id, "Other"))

  function onOther(checked: boolean) {
    setOtherOn(checked)
  }

  return (
    <Form.Group className="mb-3" data-choice="true" ref={groupRef} onChange={() => {
      const other = groupRef.current?.querySelector<HTMLInputElement>('[data-other="true"]')
      setOtherOn(Boolean(other?.checked))
    }}>
      {shown && <Form.Label>{shown}{field.required ? ' *' : ''}</Form.Label>}
      {field.options.map((option, index) => (
        <Form.Check key={`${option}-${index}`} id={`${field.id}-option-${index}`} type={field.type === "checklist" ? "checkbox" : "radio"} name={field.id} value={option} label={option} defaultChecked={answerIncludes(answers, field.id, option)} />
      ))}
      {field.allowOther && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <div className="form-check mb-0">
            <input
              className="form-check-input"
              type={field.type === "checklist" ? "checkbox" : "radio"}
              defaultChecked={answerIncludes(answers, field.id, "Other")}
              name={field.id}
              value="Other"
              id={`${field.id}-other`}
              data-other="true"
              onChange={(event) => onOther(event.target.checked)}
            />
            <label className="form-check-label" htmlFor={`${field.id}-other`}>Other</label>
          </div>
          <Form.Control autoComplete="off"
            name={`${field.id}.other`}
            defaultValue={answerText(answers, `${field.id}.other`)}
            disabled={!otherOn}
            data-other-text="true"
            aria-label="Other"
            style={{ maxWidth: '12rem' }}
            onChange={(event) => {
              const other = groupRef.current?.querySelector<HTMLInputElement>('[data-other="true"]')
              if (event.target.value && other && !other.checked) {
                other.checked = true
                onOther(true)
              }
            }}
          />
        </div>
      )}
    </Form.Group>
  )
}

function SelectField({ field, shown, answers }: { field: OrderField; shown: string; answers?: Answers }) {
  const [otherOn, setOtherOn] = useState(answerIncludes(answers, field.id, "Other"))
  return (
    <Form.Group className="mb-3">
      {shown && <Form.Label>{shown}{field.required ? ' *' : ''}</Form.Label>}
      <Form.Select autoComplete="off" name={field.id} defaultValue={answerText(answers, field.id)} aria-label={shown ? undefined : field.name} onChange={(event) => setOtherOn(event.target.value === 'Other')}>
        <option value="">Choose an option</option>
        {field.options.map((option) => <option key={option}>{option}</option>)}
        {field.allowOther && <option value="Other">Other</option>}
      </Form.Select>
      {field.allowOther && (
        <Form.Control autoComplete="off" className="mt-2" name={`${field.id}.other`} defaultValue={answerText(answers, `${field.id}.other`)} disabled={!otherOn} hidden={!otherOn} aria-label="Other" placeholder="Other" />
      )}
    </Form.Group>
  )
}

function ProductTable({ field, shown, answers, tableError }: { field: OrderField; shown: string; answers?: Answers; tableError?: string }) {
  const lines = tableLines(field)
  const columns = field.columns ?? []
  const container = useRef<HTMLDivElement>(null)
  const automatic = useRef(new Map<string, string>())
  const applying = useRef(false)
  function syncDefaults(changedName?: string) {
    if (!container.current || applying.current) return
    applying.current = true
    try {
      const inputs = new Map(Array.from(container.current.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input[name], select[name]')).map(input => [input.name, input]))
      const changed: Array<HTMLInputElement | HTMLSelectElement> = []
      applyTableDefaults(field, name => inputs.get(name)?.value ?? '', (name, value) => {
        const input = inputs.get(name)
        if (input) { input.value = value; changed.push(input) }
      }, automatic.current, changedName)
      for (const input of changed) input.dispatchEvent(new Event('input', { bubbles: true }))
    } finally { applying.current = false }
  }
  const answerRevision = JSON.stringify(answers ?? {})
  useEffect(() => { automatic.current.clear(); syncDefaults() }, [field, answerRevision])
  return (
    <div className="mb-3" ref={container} onInputCapture={event => {
      const input = event.target
      if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) syncDefaults(input.name)
    }} onChangeCapture={event => {
      const input = event.target
      if (input instanceof HTMLSelectElement) syncDefaults(input.name)
    }}>
      {shown && <div className="form-label">{shown}</div>}
      <div className="order-product-table-wrap">
        <table className="table table-bordered table-sm order-product-table mb-0" aria-describedby={tableError ? `${field.id}-table-error` : undefined}>
          <thead>
            <tr>
              <th scope="col" />
              {columns.map((column) => <th key={column.id} scope="col">{fieldCaption(column)}</th>)}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id}>
                <th scope="row">{line.label}</th>
                {columns.map((column) => (
                  <td key={column.id} className="fv-row">
                    <TableCell field={field} lineId={line.id} lineLabel={line.label} column={column} answers={answers} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tableError && <div id={`${field.id}-table-error`} className="invalid-feedback d-block" role="alert">{tableError}</div>}
      {field.help && <p className="text-body-secondary mt-2 mb-0">{field.help}</p>}
    </div>
  )
}

function TableCell({ field, lineId, lineLabel, column, answers }: { field: OrderField; lineId: string; lineLabel: string; column: OrderField; answers?: Answers }) {
  const name = `${field.id}.${lineId}.${column.id}`
  const label = `${lineLabel} ${fieldCaption(column)}`
  if (column.type === 'select') {
    return (
      <Form.Select autoComplete="off" name={name} defaultValue={answerText(answers, name)} aria-label={label} size="sm">
        <option value="" />
        {column.options.filter((option) => option.trim()).map((option) => <option key={option}>{option}</option>)}
      </Form.Select>
    )
  }
  if (column.type === 'decimal') {
    return <Form.Control autoComplete="off" name={name} type="text" inputMode="decimal" data-decimal="true"
      defaultValue={formatDecimalValue(answerText(answers, name))} aria-label={label} size="sm"
      onBlur={event => {
        const input = event.currentTarget
        const formatted = formatDecimalValue(input.value)
        if (input.value !== formatted) {
          input.value = formatted
          input.dispatchEvent(new Event('input', { bubbles: true }))
        }
      }} />
  }
  const type = column.type === 'email' || column.type === 'date' ? column.type : 'text'
  return <Form.Control autoComplete="off" name={name} defaultValue={answerText(answers, name)} type={type} inputMode={column.type === 'number' ? 'decimal' : undefined} aria-label={label} size="sm" />
}

function OrderFieldInput({ field, defaults, answers, tableError, pricing }: { field: OrderField; defaults?: OrderDefaults; answers?: Answers; tableError?: string; pricing?: CataloguePricingContext }) {
  if (field.type === 'productItems') return <ProductItemsField id={field.id} label={field.label} value={answers?.[field.id]} pricing={pricing} required={field.required} />
  const shown = field.label.trim()
  if (field.type === 'title' || field.type === 'heading' || field.type === 'label') {
    const text = fieldCaption(field)
    return (
      <div className="mb-3">
        {field.type === 'title' ? <h2 className="h4 mb-1">{text}</h2> : field.type === 'heading' ? <h3 className="h5 mb-1">{text}</h3> : <div className="form-label mb-0">{text}</div>}
        {field.help && <p className="text-body-secondary mb-0">{field.help}</p>}
      </div>
    )
  }
  if (field.type === 'table') return <ProductTable field={field} shown={shown} answers={answers} tableError={tableError} />
  if (field.type === 'select') {
    return <SelectField field={field} shown={shown} answers={answers} />
  }
  if (field.type === 'radio' || field.type === 'checklist') {
    return <ChoiceList field={field} shown={shown} answers={answers} />
  }
  if (field.type === 'checkbox') {
    return (
      <Form.Group className="mb-3">
        <Form.Check id={field.id} type="checkbox" name={field.id} defaultChecked={answerIncludes(answers, field.id, shown || field.name)} value={shown || field.name} label={shown || undefined} aria-label={shown ? undefined : field.name} />
      </Form.Group>
    )
  }
  return (
    <Form.Group className="mb-3">
      {shown && <Form.Label>{shown}{field.required ? ' *' : ''}</Form.Label>}
      <Form.Control autoComplete="off"
        name={field.id}
        as={field.type === 'textarea' ? 'textarea' : 'input'}
        type={field.type === 'textarea' ? undefined : field.type === 'number' ? 'text' : field.type}
        inputMode={field.type === 'number' ? 'decimal' : undefined}
        placeholder={field.placeholder}
        aria-label={shown ? undefined : field.name}
        readOnly={Boolean(field.readOnly)}
        defaultValue={answers ? answerText(answers, field.id) : field.type === 'text' || field.type === 'textarea' ? defaultFieldValue(field.defaultFrom, defaults) : undefined}
      />
    </Form.Group>
  )
}
